import { proxy, subscribe } from "valtio";
import { isEqual } from "lodash-es";
import log from "logbeacon";
import { TokenInfo, TokenState, CoinmarketCapToken } from "../types";
// {
// 	"pool": "2unlbn25tn1ptj4tui1epnmvgx27dwhrkjx1pdp2byqu",
// 	"ts": 1741078226,
// 	"vol_24h": "0.000000",
// 	"vol_24h_usdt": "0.000000",
// 	"price": "3.617577333638000872935880916E-8",
// 	"price_usdt": "0.0000000000001658629367037269969782570244",
// 	"smart_buy_vol_5m": "0.000000",
// 	"smart_buy_vol_5m_usdt": "0.000000",
// 	"smart_buy_vol_1h": "0.000000",
// 	"smart_buy_vol_1h_usdt": "0.000000",
// 	"smart_buy_vol_6h": "0.000000",
// 	"smart_buy_vol_6h_usdt": "0.000000",
// 	"smart_buy_vol_24h": "0.000000",
// 	"smart_buy_vol_24h_usdt": "0.000000",
// 	"smart_sell_vol_5m": "0.000000",
// 	"smart_sell_vol_5m_usdt": "0.000000",
// 	"smart_sell_vol_1h": "0.000000",
// 	"smart_sell_vol_1h_usdt": "0.000000",
// 	"smart_sell_vol_6h": "0.000000",
// 	"smart_sell_vol_6h_usdt": "0.000000",
// 	"smart_sell_vol_24h": "0.000000",
// 	"smart_sell_vol_24h_usdt": "0.000000",
// 	"price_change_5m": "0.00000003617577333638000872935880916",
// 	"price_change_1h": "0.00000003617577333638000872935880916",
// 	"price_change_6h": "0.00000003617577333638000872935880916",
// 	"price_change_24h": "0.00000003617577333638000872935880916",
// 	"vol_change_5m": "0",
// 	"vol_change_1h": "0",
// 	"vol_change_pre_5m": "0.000000000000000000",
// 	"vol_change_pre_1h": "0.000000000000000000"
// }

const UPDATE_INTERVAL = 2000;
export const TokenStore = proxy<TokenState>({
  tokenList: [],

  // 批量更新相关属性 - tokenList (使用Map结构，pool作为key)
  _pendingTokenUpdates: new Map<string, TokenInfo>(),
  _lastUpdateTime: 0, // 记录上次更新时间

  // 批量更新相关属性 - coinmarketcapTokenList (使用Map结构，id作为key)
  _pendingCoinmarketcapTokenUpdates: new Map<string, CoinmarketCapToken>(),
  _lastCoinmarketcapUpdateTime: 0, // 记录coinmarketcap上次更新时间

  // 添加待更新token数据到队列
  addTokenUpdate: (tokens: TokenInfo[]) => {
    if (!tokens?.length) {
      return;
    }

    // 使用Map结构，直接覆盖相同pool的数据，自动去重
    tokens.forEach((newToken) => {
      TokenStore._pendingTokenUpdates.set(newToken.pool, newToken);
    });

    // 使用时间间隔判断，必须间隔UPDATE_INTERVAL毫秒才能更新
    const currentTime = Date.now();
    const timeSinceLastUpdate = currentTime - TokenStore._lastUpdateTime;

    if (timeSinceLastUpdate >= UPDATE_INTERVAL) {
      // 立即更新
      TokenStore._processPendingTokenUpdates();
    }
    // 如果时间间隔不足，新的更新请求会被合并到_pendingTokenUpdates中，等待下次调用
  },

  // 处理待更新的token数据
  _processPendingTokenUpdates: () => {
    if (TokenStore._pendingTokenUpdates.size === 0) {
      return;
    }

    // 更新最后更新时间
    TokenStore._lastUpdateTime = Date.now();

    // 检查是否有实际的数据变化
    let hasChanges = false;
    const updatedTokenList = [...TokenStore.tokenList];

    // 处理所有待更新的token
    TokenStore._pendingTokenUpdates.forEach((newToken) => {
      // 查找已存在的token
      const existingIndex = updatedTokenList.findIndex(
        (token) => token.pool === newToken.pool,
      );

      if (existingIndex !== -1) {
        // 如果token已存在，检查是否有变化
        const existingToken = updatedTokenList[existingIndex];
        if (!isEqual(existingToken, newToken)) {
          updatedTokenList[existingIndex] = newToken;
          hasChanges = true;
        }
      } else {
        // 如果token不存在，添加到列表末尾
        updatedTokenList.push(newToken);
        hasChanges = true;
      }
    });

    // 只在数据真正有变化时才更新
    if (hasChanges) {
      TokenStore.tokenList = updatedTokenList;
    }

    // 清空待更新Map
    TokenStore._pendingTokenUpdates.clear();
  },

  // 保持原有接口兼容性，现在内部使用批量更新
  updateToken: (tokens: TokenInfo[]) => {
    TokenStore.addTokenUpdate(tokens);
  },

  // 初始化token列表
  initTokenList: (tokens: TokenInfo[]) => {
    TokenStore.tokenList = tokens;
  },

  // 清空token列表
  clearTokenList: () => {
    TokenStore.tokenList = [];
  },

  // 获取特定token的信息
  getTokenByPool: (poolId: string): TokenInfo | undefined => {
    return TokenStore.tokenList.find(
      (token: TokenInfo) => token.pool === poolId,
    );
  },

  coinmarketcapTokenList: [],

  // 添加待更新coinmarketcap token数据到队列
  addCoinmarketcapTokenUpdate: (tokens: CoinmarketCapToken[]) => {
    if (!tokens?.length) {
      return;
    }

    // 使用Map结构，直接覆盖相同id的数据，自动去重
    tokens.forEach((newToken) => {
      TokenStore._pendingCoinmarketcapTokenUpdates.set(newToken.id, newToken);
    });

    // 使用时间间隔判断，必须间隔UPDATE_INTERVAL毫秒才能更新
    const currentTime = Date.now();
    const timeSinceLastUpdate =
      currentTime - TokenStore._lastCoinmarketcapUpdateTime;

    if (timeSinceLastUpdate >= UPDATE_INTERVAL) {
      // 立即更新
      TokenStore._processPendingCoinmarketcapTokenUpdates();
    }
    // 如果时间间隔不足，新的更新请求会被合并到_pendingCoinmarketcapTokenUpdates中，等待下次调用
  },

  // 处理待更新的coinmarketcap token数据
  _processPendingCoinmarketcapTokenUpdates: () => {
    if (TokenStore._pendingCoinmarketcapTokenUpdates.size === 0) {
      return;
    }

    // 更新最后更新时间
    TokenStore._lastCoinmarketcapUpdateTime = Date.now();

    // 检查是否有实际的数据变化
    let hasChanges = false;
    const updatedCoinmarketcapTokenList = [
      ...TokenStore.coinmarketcapTokenList,
    ];

    // 处理所有待更新的token
    TokenStore._pendingCoinmarketcapTokenUpdates.forEach((newToken) => {
      // 查找已存在的token
      const existingIndex = updatedCoinmarketcapTokenList.findIndex(
        (token) => token.id === newToken.id,
      );

      if (existingIndex !== -1) {
        // 如果token已存在，检查是否有变化
        const existingToken = updatedCoinmarketcapTokenList[existingIndex];
        if (!isEqual(existingToken, newToken)) {
          updatedCoinmarketcapTokenList[existingIndex] = newToken;
          hasChanges = true;
        }
      } else {
        // 如果token不存在，添加到列表末尾
        updatedCoinmarketcapTokenList.push(newToken);
        hasChanges = true;
      }
    });

    // 只在数据真正有变化时才更新
    if (hasChanges) {
      TokenStore.coinmarketcapTokenList = updatedCoinmarketcapTokenList;
    }

    // 清空待更新Map
    TokenStore._pendingCoinmarketcapTokenUpdates.clear();
  },

  // 更新coinmarketcap token列表，现在内部使用批量更新
  updateCoinmarketcapToken: (tokens: CoinmarketCapToken[]) => {
    TokenStore.addCoinmarketcapTokenUpdate(tokens);
  },

  // 获取特定coinmarketcap token的信息
  getCoinmarketcapTokenByPool: (
    poolId: string,
  ): CoinmarketCapToken | undefined => {
    return TokenStore.coinmarketcapTokenList.find(
      (token: CoinmarketCapToken) => token.pool === poolId,
    );
  },
});

// 订阅store变化，可以在这里添加日志或其他操作
subscribe(TokenStore, () => {
  log.debug("[文案]TokenStore changed:", {
    tokenListCount: TokenStore.tokenList.length,
    coinmarketcapTokenListCount: TokenStore.coinmarketcapTokenList.length,
  });
  // 执行其他操作
});
